/* eslint-disable no-debugger */
import { AxiosResponse } from "axios";

import { ICreateMessageProps } from "../@types/Message";
import { IMessageThread } from "../@types/MessageThread";
import { IPaginatedResponse } from "../@types/PaginatedResponse";
import { api } from "./api";

export const messageService = {
  async getMessages(patientId: number): Promise<AxiosResponse> {
    const url = "/Portal/Messages";

    const urlParamsParsed = new URLSearchParams();

    urlParamsParsed.append("sortOrder", "desc");
    urlParamsParsed.append("sortColumn", "sentDate");
    urlParamsParsed.append("patientId", patientId.toString());

    const messagesResponse = await api.get<IPaginatedResponse<any>>(url, {
      params: urlParamsParsed,
    });

    return messagesResponse;
  },

  async getMessagesPaginated(
    patientId: number,
    page?: number,
    pageSize?: number
  ): Promise<AxiosResponse<{ total: number; messages: any[] }>> {
    const url = "/Portal/Messages";

    const params: Record<string, string | number> = {
      patientId,
      sortOrder: "desc",
      sortColumn: "sentDate",
    };

    if (page !== undefined && pageSize !== undefined) {
      params.skip = (page - 1) * pageSize;
      params.take = pageSize;
    }

    return api.get(url, { params });
  },

  async createMessage(
    createMessageProps: ICreateMessageProps
  ): Promise<AxiosResponse> {
    const url = `/Portal/Messages/`;
    const {
      messageTitle,
      patientId,
      messageBody,
      timeFrame,
      responseType,
      sentProvider,
      responseOptions,
      articleId,
      threadId,
    } = createMessageProps;

    const response = await api.post(url, {
      title: messageTitle,
      body: messageBody,
      patientId,
      timeFrame,
      responseType,
      sentProvider,
      responseOptions,
      articleId,
      threadId,
    });
    return response;
  },

  async respondToPatientMessage(
    messageId: string,
    messageResponse: string
  ): Promise<AxiosResponse> {
    const url = `/Portal/Messages/${messageId}/Respond`;

    const serverResponse = await api.post(url, messageResponse);
    return serverResponse;
  },

  async readPatientMessage(messageId: string): Promise<AxiosResponse> {
    const url = `/Portal/Messages/${messageId}/ReadPatientMessage`;

    const serverResponse = await api.put(url);
    return serverResponse;
  },

  // EN-3040. Every route below takes either a thread id or the id of any message in it, so the
  // deep link the patient-page table already uses (?id=<messageId>) keeps working unchanged.

  async getThread(threadId: string): Promise<AxiosResponse<IMessageThread>> {
    return api.get(`/Portal/Messages/${threadId}/Thread`);
  },

  /** Sends into an existing thread. Reopens it server-side if it had been closed. */
  async sendThreadMessage(
    threadId: string,
    body: string
  ): Promise<AxiosResponse> {
    return api.post(`/Portal/Messages/${threadId}/Messages`, { body });
  },

  async closeThread(threadId: string): Promise<AxiosResponse> {
    return api.post(`/Portal/Messages/${threadId}/Close`);
  },

  async markThreadRead(threadId: string): Promise<AxiosResponse> {
    return api.put(`/Portal/Messages/${threadId}/Thread/Read`);
  },

  async getRegisteredDevices(patientId: number): Promise<AxiosResponse> {
    const url = "/Portal/Messages/GetRegisteredDevicesForPatient";
    const urlParamsParsed = new URLSearchParams();
    urlParamsParsed.append("patientId", patientId.toString());

    const devicesResponse = await api.get<IPaginatedResponse<any>>(url, {
      params: urlParamsParsed,
    });

    return devicesResponse;
  },
};
